import * as yup from 'yup';
import { REQUIRED_MESSAGE } from './validation';
import validZipCode from '../assets/valid-zip.json';
import us_area_codes from '../assets/us_area_codes.json';
import dayjs from 'dayjs';
import { COMMON_DATE_FORMAT_DAYJS } from './date';

const date = new Date();

const transformedTableData = us_area_codes
  ? Object.values(us_area_codes).flatMap((item) => item)
  : [];
const covertedStringList = transformedTableData.map((item) => `${item}`);

export const stringRequiredSchema = yup.string().required(REQUIRED_MESSAGE);

export const numberRequiredSchema = yup
  .number()
  .typeError('Must be a number')
  .required(REQUIRED_MESSAGE);

export const websiteSchema = yup
  .string()
  .test('Is valid url', 'Provide correct url', (value) =>
    value?.length
      ? /((https?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/.test(
          value.toLowerCase(),
        )
      : true,
  );

export const positiveNumberSchema = yup
  .number()
  .typeError('Must be a number')
  .test('Is positive?', 'The number must be greater than 0', (value) =>
    value ? value > 0 : false,
  );

const numberSchema = (
  numberLength: number,
  innerDividersCount: number,
  isNullable?: boolean,
) =>
  yup
    .string()
    .test(
      'length',
      `validation.mustBeExactly.${numberLength}`,
      (val) =>
        val?.replace('_', '').length === numberLength + innerDividersCount ||
        (isNullable === true ?? val === undefined),
    );

export const phoneNumberSchema = numberSchema(10, 2);

export const phoneNumberSchemaNonRequired = numberSchema(10, 2, true);

export const zipSchema = yup
  .string()
  .test(
    'length',
    `validation.mustBeExactly5or9`,
    (val) =>
      val?.replace('_', '').length === 5 || val?.replace('_', '').length === 9,
  )
  .test('validZip', `validation.zipUSA`, (val) => {
    const findZip = validZipCode.find((zip) => zip == `${val}`);
    return !!findZip;
  });

export const phoneSchema = yup
  .string()
  .test('validPhone', `Phone must be a valid USA phone number.`, (val) => {
    const re = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im;
    return re.test(val || '');
  })
  .test('validAreaCode', `validation.areaUSA`, (val) => {
    return !!covertedStringList.find(
      (item) =>
        item ==
        val
          ?.replace(/[~`!@#$%^&*()+={}\[\];:\'\"<>.,\/\\\?-_]/g, '')
          .substring(0, 3),
    );
  });

export const zipSchemaNonRequired = yup
  .string()
  .test(
    'length',
    `validation.mustBeExactly5or9`,
    (val) =>
      val?.replace('_', '').length === 5 ||
      val?.replace('_', '').length === 9 ||
      val === undefined,
  )
  .test('validZip', `validation.zipUSA`, (val) => {
    const findZip = validZipCode.find((zip) => zip == `${val}`);
    return !!findZip;
  });

export const ssnSchema = numberSchema(9, 2);

export const einSchema = numberSchema(9, 1);

export const routingSchema = numberSchema(9, 0).matches(
  /^\d+$/,
  'Must be numeric value',
);

export const emailSchema = yup.string().email('validation.invalidEmail');

export const confirmationCodeSchema = yup
  .array()
  .test(
    'length',
    'Must be exactly 4 numbers',
    (val) => val?.filter(Boolean).length === 4,
  );

export const dateStringSchema = (
  format = COMMON_DATE_FORMAT_DAYJS,
  customMessage?: string,
) =>
  yup
    .string()
    .test(
      'invalid',
      customMessage || `validation.dateFormat.${format}`,
      (val) => dayjs(val, format, true).isValid(),
    );

export const dateStringSchemaEnroll = (
  format = COMMON_DATE_FORMAT_DAYJS,
  customMessage?: string,
) =>
  yup
    .string()
    .test(
      'invalid',
      customMessage || `validation.dateFormat.${format}`,
      (val) =>
        dayjs(val, format, true).isValid() ||
        dayjs(val, 'MM/DD/YY', true).isValid(),
    );

export const minAge18Schema = dateStringSchema().test(
  'age',
  'Must be at least 18 years old',
  (val) => dayjs().diff(val, 'year') > 17,
);

export const minAge18SchemaEnroll = dateStringSchemaEnroll().test(
  'age',
  'Must be at least 18 years old',
  (val) => dayjs().diff(val, 'year') > 17,
);

export const minAge0Schema = dateStringSchema().test(
  'age',
  'Must be at least 0 years old',
  (val) => dayjs().diff(val, 'year') > 0,
);

export const afterToday = dateStringSchema().test(
  'recurringPaymentDate',
  'Please select a date after tomorrow',
  (val) => dayjs().startOf('day').diff(val, 'day') < -1,
);

export const addressSchema = yup
  .object()
  .test('invalid', REQUIRED_MESSAGE, (val) => val === null || val?.inputValue)
  .nullable();

export const cardFieldsSchema = {
  cardExpiration: yup
    .string()
    .required(REQUIRED_MESSAGE)
    .test({
      name: 'invalid date',
      message: 'validation.invalidDate',
      test: (value) => {
        const [month] = value?.replaceAll('_', '').split('/') || [];

        return Number(month) <= 12;
      },
    })
    .test({
      name: 'Expiration date',
      message: 'validation.cardExpired',
      test: (value) => {
        const [monthString, yearString] =
          value?.replaceAll('_', '').split('/') || [];
        const currentYear = date.getFullYear();
        const currentMonth = date.getMonth();
        const month = Number(monthString);
        const year = Number(`20${yearString}`);

        return (
          Number(year) > currentYear ||
          (Number(year) === currentYear && Number(month) > currentMonth)
        );
      },
    }),
  cardCVC: yup
    .string()
    .test(
      'length',
      'Invalid CVV',
      (val) => val?.length === 3 || val?.length === 4,
    )
    .required(REQUIRED_MESSAGE),
  cardZip: zipSchema.required(REQUIRED_MESSAGE),
};

export const bankFieldsSchema = {
  persistant_access_token: yup.string().required(REQUIRED_MESSAGE),
  bank_account_id: yup.string().required(REQUIRED_MESSAGE),
};
